import React, { useState, useEffect } from 'react';
import { Clock, Plus, Trash2, Edit, X, Car, ChevronDown, ChevronUp } from 'lucide-react';
import { BookingService } from '../../lib/bookings';
import { supabase } from '../../lib/supabase';
import { ServiceAddon, getServiceAddons } from '../../lib/services';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import EditServiceModal from './EditServiceModal';
import RichTextDescription from '../RichTextDescription';
import SelectServiceAddonsModal from '../manage/SelectServiceAddonsModal';
import AddonLineItem from './AddonLineItem';
import { usePermissions } from '../../contexts/PermissionContext';
import { staffResourceId, upsellLineStamp } from '../../lib/upsellLines';

// 🚨 DEBUG LOGGER FOR EDIT SERVICE MODAL ISSUE 🚨
const logEditServiceDebug = (message: string, data?: any) => {
  console.log(`🚨 [ModifyBookingService] ${message}`, data || '');
  
  // Add to debug console if available
  if ((window as any).debugLogger) {
    (window as any).debugLogger.addLog('info', `🚨 [ModifyBookingService] ${message}`, data);
  }
};

interface ModifyBookingServiceProps {
  service: BookingService;
  bookingId: string;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  showSalesInfo?: boolean;
  canEdit?: boolean;
}

export default function ModifyBookingService({
  service,
  bookingId,
  onUpdate,
  onError,
  showSalesInfo = true,
  canEdit = true
}: ModifyBookingServiceProps) {
  const { currentResource } = usePermissions();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSelectAddonsModalOpen, setIsSelectAddonsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hasAvailableAddons, setHasAvailableAddons] = useState(false);

  // Service-level discount editor
  const [showDiscountEditor, setShowDiscountEditor] = useState(false);
  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>(
    (service as any).discount_type === 'percentage' ? 'percentage' : 'fixed'
  );
  const [discountValue, setDiscountValue] = useState<string>(
    Number((service as any).discount_value ?? 0) > 0 ? String((service as any).discount_value) : ''
  );
  const [discountSaving, setDiscountSaving] = useState(false);

  // Use showSalesInfo permission to control visibility of payment information
  const isPaymentsDisabled = !showSalesInfo;

  // Check if service has available add-ons
  useEffect(() => {
    const checkForAddons = async () => {
      if (!service.service_id) {
        setHasAvailableAddons(false);
        return;
      }

      try {
        const addons = await getServiceAddons(service.service_id);
        setHasAvailableAddons(addons.length > 0);
      } catch (error) {
        console.error('Error checking for service add-ons:', error);
        setHasAvailableAddons(false);
      }
    };

    checkForAddons();
  }, [service.service_id]);

  const handleEditService = async (updatedService: any) => {
    try {
      setLoading(true);

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      console.log('[ModifyBookingService] Starting service update with data:', {
        serviceId: service.id,
        serviceName: updatedService.name,
        addonCount: updatedService.addons?.length || 0,
        originalAddonCount: service.addons?.length || 0
      });

      // Calculate tax amount based on updated service data with proper currency rounding
      const taxAmount = updatedService.tax_exempt ? 0 : Math.round((updatedService.price * (updatedService.tax_rate || 5) / 100) * 100) / 100;
      const totalAmount = Math.round((updatedService.price + taxAmount - (service.discount_amount || 0)) * 100) / 100;

      // Create a comprehensive update payload with all needed fields
      const updatePayload: Record<string, any> = {
        name: updatedService.name.trim(),
        description: updatedService.description?.trim() || null,
        price: updatedService.price,
        duration_minutes: updatedService.duration_minutes,
        tax_exempt: updatedService.tax_exempt,
        tax_rate: updatedService.tax_exempt ? 0 : (updatedService.tax_rate || 5),
        tax_amount: taxAmount,
        total_amount: totalAmount,
        updated_at: new Date().toISOString()
      };

      if (updatedService.pricing_type && updatedService.pricing_type !== 'flat') {
        updatePayload.pricing_type = updatedService.pricing_type;
        updatePayload.unit_price = updatedService.unit_price || 0;
        updatePayload.duration_per_unit = updatedService.duration_per_unit || 0;
        updatePayload.quantity = updatedService.quantity || 0;
      }

      console.log('[ModifyBookingService] Sending update to database:', {
        serviceId: service.id,
        updateFields: Object.keys(updatePayload)
      });

      // First step: Update the main service data
      const { data, error } = await supabase
        .from('booking_services')
        .update(updatePayload)
        .eq('id', service.id)
        .select('*')
        .single();

      if (error) {
        console.error('[ModifyBookingService] Database update error:', {
          error,
          serviceId: service.id
        });
        throw error;
      }

      // Second step: Handle addons if they exist
      if (updatedService.addons) {
        // Log details about the addons we're going to process
        console.log('[ModifyBookingService] Processing service add-ons:', {
          serviceId: service.id,
          addonCount: updatedService.addons.length,
          addonIds: updatedService.addons.map((a: any) => a.id).join(', ')
        });
        
        try {
          // Delete existing add-ons first
          console.log(`[ModifyBookingService] Deleting existing add-ons for service ID ${service.id}`);
          const { error: deleteError } = await supabase
            .from('booking_service_addons')
            .delete()
            .eq('booking_service_id', service.id);

          if (deleteError) {
            console.error('[ModifyBookingService] Error deleting existing add-ons:', deleteError);
            throw deleteError;
          }

          // Insert new add-ons if any
          if (updatedService.addons.length > 0) {
            const timestamp = new Date().toISOString();
            
            const addonInserts = updatedService.addons.map((addon: any) => {
              // Make sure addon has an ID
              if (!addon.id) {
                console.warn('[ModifyBookingService] Addon missing ID, generating temporary one');
                addon.id = 'temp-' + Math.random().toString(36).substring(2, 9);
              }
              
              const addonInsert = {
                booking_service_id: service.id,
                addon_id: addon.id,
                price: addon.price || 0,
                created_at: timestamp,
                updated_at: timestamp
              };
              
              console.log('[ModifyBookingService] Preparing addon insert:', {
                addonId: addon.id,
                addonName: addon.name,
                price: addon.price
              });
              
              return addonInsert;
            });

            console.log('[ModifyBookingService] Inserting addons:', {
              count: addonInserts.length,
              serviceId: service.id
            });
            
            const { error: insertError } = await supabase
              .from('booking_service_addons')
              .insert(addonInserts);

            if (insertError) {
              console.error('[ModifyBookingService] Error inserting new add-ons:', {
                error: insertError,
                insertData: addonInserts
              });
              throw insertError;
            }
            
            console.log('[ModifyBookingService] Successfully inserted addons');
          }
        } catch (addonError) {
          console.error('[ModifyBookingService] Error updating add-ons:', addonError);
          // Continue without throwing - we'll still update the main service
        }
      }

      console.log('[ModifyBookingService] Database update successful');

      // Note: Removed PDF generation code - no longer using the old invoicing system

      // Refresh the booking data
      console.log('[ModifyBookingService] Refreshing booking data');
      await onUpdate();

      console.log('[ModifyBookingService] Update complete, closing modal');
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('[ModifyBookingService] Error in service update:', {
        error: err,
        service: service.id
      });
      onError(err instanceof Error ? err.message : 'Failed to update service');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveService = async () => {
    try {
      setLoading(true);

      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // Clean up workflows for this service before deleting
      try {
        const { deleteBookingWorkflowsByServiceId } = await import('../../lib/bookingWorkflows');
        await deleteBookingWorkflowsByServiceId(service.booking_id, service.service_id);
        console.log(`[WORKFLOW CLEANUP] Cleaned up workflows for service ${service.service_id} in booking ${service.booking_id}`);
      } catch (workflowCleanupError) {
        console.error('[WORKFLOW CLEANUP] Error cleaning up workflows:', workflowCleanupError);
        // Don't throw here - continue with service deletion even if workflow cleanup fails
      }

      // Delete the service directly
      const { error } = await supabase
        .from('booking_services')
        .delete()
        .eq('id', service.id);
      
      if (error) throw error;

      // Note: Removed PDF generation code - no longer using the old invoicing system

      await onUpdate();
    } catch (err) {
      console.error('Error removing service:', err);
      onError(err instanceof Error ? err.message : 'Failed to remove service');
    } finally {
      setLoading(false);
    }
  };

  const handleAddonsSelect = async (addons: ServiceAddon[]) => {
    try {
      setLoading(true);

      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      console.log('Starting add-ons update:', { 
        serviceId: service.id,
        bookingId: service.booking_id,
        addonsToAdd: addons 
      });

      const { data: existingAddons, error: existingError } = await supabase
        .from('booking_service_addons')
        .select('id, addon_id, price')
        .eq('booking_service_id', service.id);

      if (existingError) throw existingError;

      const existingByAddon = new Map((existingAddons || []).map((row) => [row.addon_id, row]));
      const nextIds = new Set(addons.map((addon) => addon.id));
      const removeIds = (existingAddons || []).filter((row) => !nextIds.has(row.addon_id)).map((row) => row.id);

      if (removeIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('booking_service_addons')
          .delete()
          .in('id', removeIds);
        if (deleteError) throw deleteError;
      }

      const fresh = addons.filter((addon) => !existingByAddon.has(addon.id));
      if (fresh.length > 0) {
        const { error: insertError } = await supabase
          .from('booking_service_addons')
          .insert(
            fresh.map((addon) => ({
              booking_service_id: service.id,
              addon_id: addon.id,
              price: addon.price,
              ...upsellLineStamp(staffResourceId(currentResource)),
            }))
          );
        if (insertError) throw insertError;
      }

      for (const addon of addons) {
        const previous = existingByAddon.get(addon.id);
        if (previous && Number(previous.price) !== Number(addon.price)) {
          const { error: priceError } = await supabase
            .from('booking_service_addons')
            .update({ price: addon.price })
            .eq('id', previous.id);
          if (priceError) throw priceError;
        }
      }

      console.log('Successfully updated add-ons');

      await onUpdate();
      setIsSelectAddonsModalOpen(false);
    } catch (err) {
      console.error('Error updating add-ons:', err);
      onError(err instanceof Error ? err.message : 'Failed to update add-ons');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAddon = async (addonId: string) => {
    try {
      setLoading(true);

      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // Delete the specific add-on
      const { error: deleteError } = await supabase
        .from('booking_service_addons')
        .delete()
        .eq('booking_service_id', service.id)
        .eq('addon_id', addonId);

      if (deleteError) {
        console.error('Error deleting add-on:', deleteError);
        throw deleteError;
      }

      await onUpdate();
    } catch (err) {
      console.error('Error removing add-on:', err);
      onError(err instanceof Error ? err.message : 'Failed to remove add-on');
    } finally {
      setLoading(false);
    }
  };

  // Save a service-level discount. Stores type + value (for the new invoice
  // engine) and the resolved dollar amount (for the legacy booking totals).
  const handleSaveDiscount = async () => {
    if (!supabase) { onError('Supabase client is not initialized'); return; }
    setDiscountSaving(true);
    try {
      const val = Math.max(parseFloat(discountValue) || 0, 0);
      const price = service.price || 0;
      let resolved = discountType === 'percentage' ? (price * val) / 100 : val;
      resolved = Math.min(Math.max(resolved, 0), price);
      resolved = Math.round(resolved * 100) / 100;

      const { error } = await supabase
        .from('booking_services')
        .update({
          discount_type: discountType,
          discount_value: val,
          discount_amount: resolved,
          updated_at: new Date().toISOString(),
        })
        .eq('id', service.id);
      if (error) throw error;

      await onUpdate();
      setShowDiscountEditor(false);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to save discount');
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleClearDiscount = async () => {
    if (!supabase) return;
    setDiscountSaving(true);
    try {
      const { error } = await supabase
        .from('booking_services')
        .update({
          discount_type: 'fixed',
          discount_value: 0,
          discount_amount: 0,
          updated_at: new Date().toISOString(),
        })
        .eq('id', service.id);
      if (error) throw error;

      setDiscountValue('');
      setDiscountType('fixed');
      await onUpdate();
      setShowDiscountEditor(false);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to remove discount');
    } finally {
      setDiscountSaving(false);
    }
  };

  const appliedDiscountLabel = (() => {
    const dv = Number((service as any).discount_value ?? 0);
    if (dv <= 0) return null;
    return (service as any).discount_type === 'percentage'
      ? `−${dv}%`
      : `−$${Number(service.discount_amount || 0).toFixed(2)}`;
  })();

  const serviceDiscountAmount = Number(service.discount_amount || 0);
  const hasServiceDiscount = serviceDiscountAmount > 0;
  const netServicePrice = Math.max((service.price || 0) - serviceDiscountAmount, 0);
  const money2 = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // Display-only compare-at: when set, it wins the strikethrough slot over the pre-discount price.
  const compareAtRaw = (service as any).compare_at_price;
  const hasCompareAt = compareAtRaw != null && Number(compareAtRaw) > (service.price || 0);
  const struckPrice = hasCompareAt ? Number(compareAtRaw) : (hasServiceDiscount ? (service.price || 0) : null);

  return (
    <>
      <div className="flex items-start justify-between py-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[14px] font-semibold text-gray-900" style={{ 
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              letterSpacing: '-0.01em'
            }}>
              {service.name}
            </span>
            {(service.pricing_type === 'per_unit' || service.pricing_type === 'per_sqft') && (
              <span className="text-[9px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                {service.pricing_type === 'per_sqft' ? 'PER SQ FT' : 'PER UNIT'}
              </span>
            )}
          </div>
          {service.description && (
            <RichTextDescription description={service.description} className="text-[12px] text-gray-600 mb-1.5 leading-relaxed" />
          )}
          {(service.pricing_type === 'per_unit' || service.pricing_type === 'per_sqft') && (service.quantity || 0) > 0 && (
            <div className="text-[11px] text-gray-500 mb-0.5">
              {(service.quantity || 0).toLocaleString()} {service.pricing_type === 'per_sqft' ? 'sq ft' : 'units'} × ${(service.unit_price || 0).toFixed(2)}/{service.pricing_type === 'per_sqft' ? 'sqft' : 'unit'}
            </div>
          )}
          <div className="flex items-center text-[11px] text-gray-500">
            <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
            {(() => {
              if (service.duration_minutes >= 1440) {
                return `${Math.floor(service.duration_minutes / 1440)} days`;
              } else if (service.duration_minutes >= 60) {
                const hours = Math.floor(service.duration_minutes / 60);
                const mins = service.duration_minutes % 60;
                return mins > 0 ? `${hours} hr ${mins} min` : `${hours} hours`;
              } else {
                return `${service.duration_minutes} minutes`;
              }
            })()}
          </div>
          {/* Display add-ons if any - iOS Style */}
          {service.addons && service.addons.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200/60">
              <div className="text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Add-ons</div>
              <div className="space-y-1.5">
                {service.addons.map((addon) => (
                  <AddonLineItem
                    key={addon.id}
                    addon={addon}
                    bookingServiceId={service.id}
                    canEdit={!!canEdit}
                    isPaymentsDisabled={isPaymentsDisabled}
                    parentLoading={loading}
                    onUpdate={onUpdate}
                    onError={onError}
                    onRemove={handleRemoveAddon}
                  />
                ))}
              </div>
            </div>
          )}
          {/* Action Buttons - iOS Style */}
          {canEdit && (
            <div className="flex items-center space-x-2 mt-2.5">
              <button
                onClick={() => {
                  logEditServiceDebug('🔧 EDIT BUTTON CLICKED - About to open EditServiceModal', {
                    serviceId: service.id,
                    serviceName: service.name,
                    bookingId: bookingId
                  });
                  setIsEditModalOpen(true);
                }}
                disabled={loading || service.status === 'cancelled'}
                className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-[8px] text-[11px] font-medium hover:bg-blue-100 active:bg-blue-200 active:scale-95 transition-all duration-150 touch-manipulation disabled:opacity-50"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Edit
              </button>
              {hasAvailableAddons && (
                <button
                  onClick={() => setIsSelectAddonsModalOpen(true)}
                  disabled={loading || service.status === 'cancelled'}
                  className="px-3 py-1.5 bg-gray-50 text-gray-700 rounded-[8px] text-[11px] font-medium hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation disabled:opacity-50"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Add-ons
                </button>
              )}
              <button
                onClick={() => setShowDiscountEditor((v) => !v)}
                disabled={loading || service.status === 'cancelled'}
                className={`px-3 py-1.5 rounded-[8px] text-[11px] font-medium active:scale-95 transition-all duration-150 touch-manipulation disabled:opacity-50 ${
                  appliedDiscountLabel ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Discount
              </button>
              <button
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={loading || service.status === 'cancelled'}
                className="px-3 py-1.5 bg-red-50 text-red-600 rounded-[8px] text-[11px] font-medium hover:bg-red-100 active:bg-red-200 active:scale-95 transition-all duration-150 touch-manipulation disabled:opacity-50"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Remove
              </button>
            </div>
          )}

          {/* Service-level discount editor */}
          {canEdit && showDiscountEditor && (
            <div className="mt-2 p-2.5 bg-gray-50 border border-gray-200 rounded-[10px]">
              <div className="flex items-center gap-2">
                <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setDiscountType('fixed')}
                    className={`px-2.5 py-1.5 text-[12px] font-semibold ${discountType === 'fixed' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}
                  >
                    $
                  </button>
                  <button
                    type="button"
                    onClick={() => setDiscountType('percentage')}
                    className={`px-2.5 py-1.5 text-[12px] font-semibold ${discountType === 'percentage' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}
                  >
                    %
                  </button>
                </div>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountType === 'percentage' ? 'e.g. 10' : 'e.g. 25.00'}
                  className="flex-1 min-w-0 px-2.5 py-1.5 text-[13px] bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  style={{ fontSize: '16px' }}
                />
                <button
                  type="button"
                  onClick={handleSaveDiscount}
                  disabled={discountSaving}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-[12px] font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50 flex-shrink-0"
                >
                  {discountSaving ? '…' : 'Save'}
                </button>
              </div>
              {Number((service as any).discount_value ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={handleClearDiscount}
                  disabled={discountSaving}
                  className="mt-2 text-[11px] font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  Remove discount
                </button>
              )}
            </div>
          )}
        </div>
        {/* Price - iOS Style */}
        {!isPaymentsDisabled && (
          <div className="ml-3 flex-shrink-0 text-right" style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            letterSpacing: '-0.01em'
          }}>
            {struckPrice != null ? (
              <>
                <div className="text-[15px] font-semibold text-gray-900">
                  ${money2(netServicePrice)}
                </div>
                <div className="flex items-center justify-end gap-1.5 mt-0.5">
                  {appliedDiscountLabel && (
                    <span className="text-[11px] font-medium text-green-600">{appliedDiscountLabel}</span>
                  )}
                  <span className="text-[11px] text-gray-400 line-through">
                    ${money2(struckPrice)}
                  </span>
                  {hasServiceDiscount && canEdit && (
                    <button
                      type="button"
                      onClick={handleClearDiscount}
                      disabled={discountSaving}
                      title="Remove discount"
                      className="h-5 w-5 flex items-center justify-center text-red-500 bg-red-50 hover:bg-red-100 rounded-full active:scale-95 transition-all disabled:opacity-50 flex-shrink-0"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="text-[15px] font-semibold text-gray-900">
                ${service.price.toLocaleString()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 🚀 FIX: Use React Portal to render EditServiceModal outside parent DOM tree */}
      {isEditModalOpen && (
        <EditServiceModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          service={{
            name: service.name || '',
            description: service.description || '',
            price: service.price || 0,
            duration_minutes: service.duration_minutes || 0,
            is_active: true,
            tax_rate: service.tax_rate || 5,
            tax_exempt: service.tax_exempt || false,
            pricing_type: service.pricing_type || 'flat',
            unit_price: service.unit_price || 0,
            duration_per_unit: service.duration_per_unit || 0,
            quantity: service.quantity || 0,
            addons: Array.isArray(service.addons) ? service.addons.map(addon => ({
              id: addon.id,
              name: addon.name || '',
              description: addon.description || '',
              price: addon.price || 0,
              duration_minutes: addon.duration_minutes || 0,
              order_index: addon.order_index || 0,
              service_id: service.service_id || '',
              addon_service_id: addon.addon_service_id || addon.id || '',
              created_at: addon.created_at || new Date().toISOString(),
              updated_at: addon.updated_at || new Date().toISOString()
            })) : []
          }}
          onSave={handleEditService}
        />
      )}

      <SelectServiceAddonsModal
        isOpen={isSelectAddonsModalOpen}
        onClose={() => setIsSelectAddonsModalOpen(false)}
        onSelect={handleAddonsSelect}
        serviceId={service.service_id}
        selectedAddons={service.addons || []}
      />
      
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={async () => {
          try {
            setDeleteLoading(true);
            await handleRemoveService();
            setIsDeleteModalOpen(false);
          } finally {
            setDeleteLoading(false);
          }
        }}
        title="Remove Service"
        message="Are you sure you want to remove this service? This action cannot be undone."
        loading={deleteLoading}
      />
    </>
  );
}